import { useState } from "react";
import { G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, calcPriority } from "../constants";
import { FEED_HEALTH, GSC_SOURCES, TAXONOMY_DATA, CONSISTENCY_CHECKS } from "../data";
import { BigRing, Ring, SevBadge, Tag } from "../components/shared";
import { PageHead, Footer } from "../components/Shell";

export function S03({ sel, setSel, onBack, onNext, onSkipGmc, products }) {
  const [view, setView] = useState("overview"); // "overview" | "taxonomy" | "consistency" | "skus"
  const all = sel.length === products.length;
  const fh  = FEED_HEALTH;

  const sevColor = s => s === "high" ? "#dc2626" : s === "med" ? "#d97706" : "#6b7280";
  const sevBg    = s => s === "high" ? "#fef2f2" : s === "med" ? "#fffbeb" : "#f9fafb";
  const sevBorder= s => s === "high" ? "#fecaca" : s === "med" ? "#fde68a" : BORDER;
  const sevLabel = s => s === "high" ? "Critical" : s === "med" ? "Warning" : "Low";

  const scoreColor = n => n >= 70 ? G : n >= 45 ? "#d97706" : "#dc2626";

  const consistencyIssues = CONSISTENCY_CHECKS;
  const highConsistency = consistencyIssues.filter(c => c.severity === "high").length;

  return (
    <>
      <PageHead
        num="03" title="Feed Audit"
        sub={view === "overview" ? "Shopify product feed health across your catalogue - content, metafields, taxonomy and enrichment gaps."
          : view === "taxonomy" ? "Auto-classified Google Product Categories per SKU - verify and apply before syncing to GMC."
          : view === "consistency" ? "Cross-SKU checks for naming, attribute patterns and structural consistency across your catalogue."
          : "Select SKUs to process in this batch."}
        onBack={view === "overview" ? onBack : () => setView("overview")}
        onNext={view === "skus" ? onNext : () => setView("skus")}
        nextLabel={view === "skus" ? `Fix GMC Issues (${sel.length}) \u2192` : "Review SKUs \u2192"}
        disabled={view === "skus" && sel.length === 0}
      />

      {/* Tab switcher */}
      <div data-testid="audit-tabs" style={{ display: "flex", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 7, overflow: "hidden", marginBottom: 20, width: "fit-content" }}>
        {[
          { key: "overview",     label: "Feed Health Overview" },
          { key: "taxonomy",     label: "Taxonomy" },
          { key: "consistency",  label: `Consistency (${consistencyIssues.length})` },
          { key: "skus",         label: "SKU Selection" },
        ].map(t => (
          <button key={t.key} data-testid={`audit-tab-${t.key}`} onClick={() => setView(t.key)}
            style={{ border: "none", background: view === t.key ? BLACK : BG, color: view === t.key ? "#fff" : MID, padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW VIEW ── */}
      {view === "overview" && (
        <>
          {/* Overall score hero */}
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "24px", marginBottom: 20, background: "#fafafa", alignItems: "center" }}>
            {/* Big ring */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <BigRing score={fh.score} animated={true} size={120} strokeWidth={10} />
              <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(fh.score), textAlign: "center" }}>
                {fh.score < 45 ? "Poor" : fh.score < 70 ? "Needs work" : "Good"}
              </div>
            </div>

            {/* Summary text + breakdown */}
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: BLACK, marginBottom: 4, letterSpacing: "-0.3px" }}>
                Shopify product feed health
              </div>
              <div style={{ fontSize: 13, color: MID, marginBottom: 16, lineHeight: 1.6 }}>
                Across <strong>{fh.totalSkus} SKUs</strong> in your catalogue. Score is weighted across title quality, description coverage, metafield completeness, taxonomy and tagging, SEO fields and signal coverage. GMC-specific issues are audited separately in step 05.
              </div>

              {/* Score breakdown bars */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                {fh.scoreBreakdown.map(b => (
                  <div key={b.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: MID }}>{b.label}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(b.score) }}>{b.score}/100</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: BORDER, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${b.score}%`, background: scoreColor(b.score), borderRadius: 3, transition: "width 0.6s ease" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Issue list */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.8px" }}>Issues identified - {fh.issues.length} across feed</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["high","med","low"].map(s => {
                  const n = fh.issues.filter(i => i.sev === s).length;
                  return n > 0 && (
                    <span key={s} style={{ fontSize: 11, fontWeight: 700, color: sevColor(s), background: sevBg(s), border: `1px solid ${sevBorder(s)}`, borderRadius: 4, padding: "2px 8px" }}>
                      {n} {sevLabel(s)}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Category grouping */}
            {["Content", "Metafields", "Taxonomy", "SEO", "Enrichment"].map(cat => {
              const catIssues = fh.issues.filter(i => i.category === cat);
              if (!catIssues.length) return null;
              const catColor = { Content: "#4285f4", Metafields: G, Taxonomy: "#6c63ff", SEO: "#f59e0b", Enrichment: "#ec4899" }[cat];
              const catBg    = { Content: "#eff6ff", Metafields: GL,  Taxonomy: "#f5f3ff", SEO: "#fffbeb", Enrichment: "#fdf2f8" }[cat];
              return (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: catColor, background: catBg, border: `1px solid ${catColor}30`, borderRadius: 4, padding: "2px 9px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{cat}</span>
                    <div style={{ flex: 1, height: 1, background: BORDER }} />
                    <span style={{ fontSize: 11, color: LIGHT }}>{catIssues.length} issue{catIssues.length > 1 ? "s" : ""}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {catIssues.map((issue, i) => (
                      <div key={`issue-${issue.label}`} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", border: `1px solid ${sevBorder(issue.sev)}`, borderLeft: `3px solid ${sevColor(issue.sev)}`, borderRadius: 7, background: BG }}>
                        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{issue.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{issue.label}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, color: sevColor(issue.sev), background: sevBg(issue.sev), border: `1px solid ${sevBorder(issue.sev)}`, borderRadius: 3, padding: "1px 6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>{sevLabel(issue.sev)}</span>
                            <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 800, color: sevColor(issue.sev) }}>{issue.count} SKUs</span>
                          </div>
                          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.55 }}>{issue.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
            <button onClick={onBack} style={{ border: `1px solid ${BORDER}`, background: BG, borderRadius: 6, padding: "8px 18px", fontSize: 13, color: MID, cursor: "pointer", fontFamily: "inherit" }}>&larr; Back</button>
            <button onClick={() => setView("skus")} style={{ background: G, color: "#fff", border: "none", borderRadius: 6, padding: "8px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Review SKUs &rarr;</button>
          </div>
        </>
      )}

      {/* ── TAXONOMY TAB ── */}
      {view === "taxonomy" && (
        <div data-testid="taxonomy-tab" style={{ marginBottom: 20 }}>
          {/* Taxonomy intro */}
          <div style={{ padding: "14px 18px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 6, background: "#ede9fe", border: "1px solid #ddd6fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#6c63ff", flexShrink: 0 }}>T</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#3730a3", marginBottom: 2 }}>Google Product Category Auto-Classification</div>
              <div style={{ fontSize: 12, color: "#4c1d95", lineHeight: 1.5 }}>SIFT analyses product type, tags, material, and signal data to suggest the most specific Google taxonomy path. Accurate classification improves Shopping filter eligibility and ad targeting.</div>
            </div>
          </div>

          {/* Per-SKU taxonomy cards */}
          {products.map(p => {
            const tax = TAXONOMY_DATA[p.id];
            if (!tax) return null;
            const conf = tax.suggested.confidence;
            const confColor = conf >= 90 ? G : conf >= 70 ? "#d97706" : "#dc2626";

            return (
              <div key={p.id} data-testid={`taxonomy-${p.id}`} style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                {/* Header */}
                <div style={{ padding: "12px 16px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{p.title}</span>
                    <span style={{ fontSize: 11, color: LIGHT }}>{p.sku}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>Current type:</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: tax.current.googleCategory ? G : "#d97706", background: tax.current.googleCategory ? GL : "#fffbeb", border: `1px solid ${tax.current.googleCategory ? GB : "#fde68a"}`, borderRadius: 4, padding: "2px 8px" }}>
                      {tax.current.productType || "Not set"}{!tax.current.googleCategory && " (no Google category)"}
                    </span>
                  </div>
                </div>

                {/* Suggested classification */}
                <div style={{ padding: "14px 16px", background: `${confColor}08`, borderBottom: `1px solid ${BORDER}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: confColor, textTransform: "uppercase", letterSpacing: "0.6px" }}>Suggested</span>
                    <div style={{ flex: 1, height: 1, background: BORDER }} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: confColor }}>{conf}% confidence</span>
                  </div>

                  {/* Category path breadcrumb */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                    {tax.suggested.path.split(" > ").map((seg, i, arr) => (
                      <span key={`seg-${seg}`} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: i === arr.length - 1 ? 700 : 500, color: i === arr.length - 1 ? BLACK : MID, background: i === arr.length - 1 ? `${confColor}15` : "transparent", border: i === arr.length - 1 ? `1px solid ${confColor}30` : "none", borderRadius: 4, padding: i === arr.length - 1 ? "2px 8px" : "0" }}>
                          {seg}
                        </span>
                        {i < arr.length - 1 && <span style={{ fontSize: 11, color: LIGHT }}>&rsaquo;</span>}
                      </span>
                    ))}
                    <span style={{ fontSize: 10, color: LIGHT, marginLeft: 6 }}>ID: {tax.suggested.googleId}</span>
                  </div>

                  {/* Confidence signals */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {tax.suggested.signals.map((s) => (
                      <span key={`tax-sig-${s}`} style={{ fontSize: 10, fontWeight: 600, color: "#4c1d95", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 4, padding: "2px 8px" }}>{s}</span>
                    ))}
                  </div>

                  {/* Confidence bar */}
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: BORDER, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${conf}%`, background: confColor, borderRadius: 3, transition: "width 0.6s ease" }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: confColor, minWidth: 30 }}>{conf}%</span>
                  </div>
                </div>

                {/* Alternatives */}
                {tax.alternatives.length > 0 && (
                  <div style={{ padding: "10px 16px", background: "#f9fafb" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 6 }}>Alternatives</div>
                    {tax.alternatives.map((alt) => (
                      <div key={`alt-${alt.googleId}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 11, color: MID }}>{alt.path}</span>
                          <span style={{ fontSize: 10, color: LIGHT }}>ID: {alt.googleId}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: alt.confidence >= 70 ? G : "#d97706" }}>{alt.confidence}%</span>
                          <span style={{ fontSize: 10, color: LIGHT, fontStyle: "italic" }}>{alt.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary */}
          <div style={{ padding: "10px 16px", background: GL, border: `1px solid ${GB}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: G }}>
              {products.filter(p => TAXONOMY_DATA[p.id]?.suggested.confidence >= 90).length} of {products.length} SKUs classified with {"\u2265"}90% confidence
            </span>
            <span style={{ fontSize: 11, color: MID }}>— these can be auto-applied to your Google Merchant Centre feed</span>
          </div>
        </div>
      )}

      {/* ── CONSISTENCY TAB ── */}
      {view === "consistency" && (
        <div data-testid="consistency-tab" style={{ marginBottom: 20 }}>
          {/* Consistency intro */}
          <div style={{ padding: "14px 18px", background: highConsistency > 0 ? "#fef2f2" : "#fffbeb", border: `1px solid ${highConsistency > 0 ? "#fecaca" : "#fde68a"}`, borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 6, background: highConsistency > 0 ? "#fee2e2" : "#fef3c7", border: `1px solid ${highConsistency > 0 ? "#fecaca" : "#fde68a"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: highConsistency > 0 ? "#dc2626" : "#d97706", flexShrink: 0 }}>
              {consistencyIssues.length}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: highConsistency > 0 ? "#991b1b" : "#92400e", marginBottom: 2 }}>Catalogue Consistency Check</div>
              <div style={{ fontSize: 12, color: highConsistency > 0 ? "#991b1b" : "#92400e", lineHeight: 1.5 }}>
                {highConsistency > 0
                  ? `${highConsistency} critical inconsistencies found across your catalogue. These affect Shopping feed quality, structured data, and brand coherence.`
                  : "Minor consistency issues detected. Fixing these improves feed quality and brand coherence across channels."}
              </div>
            </div>
          </div>

          {/* Group by type */}
          {["naming", "attribute", "structure"].map(type => {
            const typeIssues = consistencyIssues.filter(c => c.type === type);
            if (!typeIssues.length) return null;
            const typeLabel = { naming: "Naming Conventions", attribute: "Attribute Coverage", structure: "Structural Patterns" }[type];
            const typeColor = { naming: "#4285f4", attribute: "#6c63ff", structure: "#f59e0b" }[type];
            const typeBg = { naming: "#eff6ff", attribute: "#f5f3ff", structure: "#fffbeb" }[type];

            return (
              <div key={type} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: typeColor, background: typeBg, border: `1px solid ${typeColor}30`, borderRadius: 4, padding: "2px 9px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{typeLabel}</span>
                  <div style={{ flex: 1, height: 1, background: BORDER }} />
                  <span style={{ fontSize: 11, color: LIGHT }}>{typeIssues.length} issue{typeIssues.length > 1 ? "s" : ""}</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {typeIssues.map((issue, i) => (
                    <div key={`consist-${issue.label}`} data-testid={`consistency-issue-${i}`} style={{ border: `1px solid ${sevBorder(issue.severity)}`, borderLeft: `3px solid ${sevColor(issue.severity)}`, borderRadius: 8, overflow: "hidden", background: BG }}>
                      <div style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: BLACK }}>{issue.label}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: sevColor(issue.severity), background: sevBg(issue.severity), border: `1px solid ${sevBorder(issue.severity)}`, borderRadius: 3, padding: "1px 6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>{sevLabel(issue.severity)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 8 }}>{issue.detail}</div>

                        {/* Affected SKUs */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>Affected:</span>
                          {issue.affected.map(sku => (
                            <span key={sku} style={{ fontSize: 10, fontWeight: 600, color: MID, background: "#f3f4f6", border: `1px solid ${BORDER}`, borderRadius: 3, padding: "2px 7px" }}>{sku}</span>
                          ))}
                        </div>

                        {/* Recommendation */}
                        <div style={{ padding: "8px 12px", background: GL, border: `1px solid ${GB}`, borderRadius: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.4px" }}>Fix: </span>
                          <span style={{ fontSize: 11, color: G }}>{issue.recommendation}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Consistency score summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}>
            {[
              { label: "Critical", count: consistencyIssues.filter(c => c.severity === "high").length, color: "#dc2626", bg: "#fef2f2" },
              { label: "Warning", count: consistencyIssues.filter(c => c.severity === "med").length, color: "#d97706", bg: "#fffbeb" },
              { label: "Low", count: consistencyIssues.filter(c => c.severity === "low").length, color: "#6b7280", bg: "#f9fafb" },
            ].map((s, i) => (
              <div key={s.label} style={{ padding: "14px 16px", textAlign: "center", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", background: s.bg }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color, marginBottom: 2 }}>{s.count}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: s.color, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SKU SELECTION VIEW ── */}
      {view === "skus" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: MID }}>{sel.length} of {products.length} selected for this batch</span>
            <button onClick={() => setSel(all ? [] : products.map(p => p.id))} style={{ background: "none", border: "none", color: G, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{all ? "Deselect all" : "Select all"}</button>
          </div>

          <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 90px 80px 80px 90px 100px", padding: "9px 16px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, gap: 0 }}>
              {["", "Product / SKU", "Feed Score", "Issues", "Signals", "Priority", "Sources"].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</div>
              ))}
            </div>

            {products.map((p, i) => {
              const on  = sel.includes(p.id);
              const pri = calcPriority(p);
              return (
                <div key={p.id} onClick={() => setSel(on ? sel.filter(x => x !== p.id) : [...sel, p.id])}
                  style={{ display: "grid", gridTemplateColumns: "32px 1fr 90px 80px 80px 90px 100px", alignItems: "center", padding: "13px 16px", cursor: "pointer", borderBottom: i < products.length - 1 ? `1px solid ${BORDER}` : "none", background: on ? GL : BG, borderLeft: `3px solid ${on ? G : "transparent"}`, transition: "background 0.1s", gap: 0 }}>
                  <div style={{ width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${on ? G : BORDER}`, background: on ? G : BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {on && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.title}</div>
                    <div style={{ fontSize: 11, color: LIGHT, marginTop: 1 }}>{p.sku} · {p.brand}</div>
                  </div>
                  <Ring score={p.feedScore} size={40} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: p.issueCount >= 4 ? "#dc2626" : p.issueCount >= 2 ? "#d97706" : G }}>{p.issueCount} issue{p.issueCount !== 1 ? "s" : ""}</div>
                  <div style={{ fontSize: 13, color: MID }}>{Object.values(p.signals || {}).reduce((a, b) => a + b, 0)}</div>
                  <div title={`Priority score: ${pri.score}/100 - weighted by feed gap (40%), signal richness (35%), revenue opportunity (25%)`}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: pri.color, background: pri.bg, border: `1px solid ${pri.border}`, borderRadius: 4, padding: "2px 8px", cursor: "help" }}>{pri.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {Object.entries(p.signals || {}).map(([k, v]) => (
                      <div key={k} title={`${GSC_SOURCES[k]?.label || k}: ${v}${GSC_SOURCES[k]?.beta ? " (Beta)" : ""}`} style={{ width: 8, height: 8, borderRadius: "50%", background: GSC_SOURCES[k]?.color, opacity: v > 0 ? 1 : (GSC_SOURCES[k]?.beta ? 0.15 : 0.2), border: GSC_SOURCES[k]?.beta ? "1px dashed rgba(0,0,0,0.15)" : "none" }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* GMC optional notice */}
          <div style={{ marginTop: 14, padding: "12px 16px", background: "#fffbeb", border: `1px solid #fde68a`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>GMC Fix is optional - </span>
              <span style={{ fontSize: 12, color: "#92400e" }}>skip if you don't have Google Merchant Centre connected, or want to optimise copy only.</span>
            </div>
            <button onClick={onSkipGmc} disabled={sel.length === 0}
              style={{ background: sel.length === 0 ? "#e5e7eb" : "#fff", color: sel.length === 0 ? LIGHT : "#92400e", border: `1px solid ${sel.length === 0 ? BORDER : "#fde68a"}`, borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: sel.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              Skip GMC Fix →
            </button>
          </div>

          <Footer onBack={() => setView("overview")} onNext={onNext} nextLabel={`Fix GMC Issues (${sel.length} SKU${sel.length !== 1 ? "s" : ""}) →`} disabled={sel.length === 0} />
        </>
      )}
    </>
  );
}


// ─── STEP 06 ─────────────────────────────────────────────────────────────────

